import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react"
import { Suspense, useState } from "react"

import {
  getImageLabel,
  type ImageBlockData,
  MAIN_THUMBNAIL_NAME
} from "../../../../core/imageUtils"
import { EmptyCardContent } from "../../../ImageField/Components/EmptyCardContent"
import { ImageFieldCard } from "../../../ImageField/Components/ImageFieldCard"
import { NonEmptyCardContent } from "../../../ImageField/Components/NonEmptyCardContent"
import { useSelected } from "../useSelected"
import { useImageView } from "./useImageView"

interface ImageNode extends ProseMirrorNode {
  attrs: { content: ImageBlockData | undefined }
}
export interface ImageViewProps extends NodeViewProps {
  node: ImageNode
}

export function ImageView(props: ImageViewProps) {
  const { node } = props
  const content = node.attrs.content
  const { selected } = useSelected(props)

  const thumbnail = {
    width: undefined,
    height: undefined,
    name: MAIN_THUMBNAIL_NAME
  }

  const {
    onMediaSelected,
    onCropperDialogClose,
    isUpdating,
    updateContent,
    onClear
  } = useImageView({ content, thumbnail, imageViewProps: props })

  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false)

  const fieldLabel = "Feature image"

  const isImageEmpty = content?.width === 0 && content?.height === 0

  const imageLabel =
    content && !isImageEmpty ? [`${content.width}×${content.height}px`] : []

  const image = {
    thumbnail,
    contentView: content,
    labels: getImageLabel(thumbnail, content, content)
  }

  return (
    <NodeViewWrapper className="custom-image">
      <div contentEditable="false" suppressContentEditableWarning>
        <ImageFieldCard
          isMediaDialogOpen={isMediaDialogOpen}
          selected={selected}
          onMediaSelected={onMediaSelected}
          onMediaDialogOpenChange={setIsMediaDialogOpen}
        >
          <Suspense fallback={fallback}>
            {content?.url ? (
              <NonEmptyCardContent
                allContentViews={[content]}
                content={content}
                contentView={content}
                fieldLabel={fieldLabel}
                id="id"
                imageLabel={imageLabel}
                initialImages={[image]}
                isUpdating={isUpdating}
                readOnly={false}
                thumbnailName={thumbnail.name}
                onClear={onClear}
                onContentChange={updateContent}
                onCropperDialogClose={onCropperDialogClose}
                onMediaDialogOpenChange={setIsMediaDialogOpen}
              />
            ) : (
              <EmptyCardContent
                fieldLabel={fieldLabel}
                isUpdating={isUpdating}
                readOnly={false}
                onMediaDialogOpenChange={setIsMediaDialogOpen}
              />
            )}
          </Suspense>
        </ImageFieldCard>
      </div>
    </NodeViewWrapper>
  )
}

//reserves height in-between loading images
const fallback = <div style={{ height: 144 }} />
